/**
 * firstNameFor: the name a greeting may use, or nothing.
 *
 * The Flow home said "Good evening, CA" on 23 Sep 2026. It took the first
 * whitespace token of `full_name`, and two things put the wrong token there:
 * the signup trigger stores the email as the name until the person types one
 * (`00004_user_trigger.sql:13`), and professionals write their title first
 * ("CA Ramesh Sharma"). A greeting that reads out an email address or a
 * qualification is worse than no name, so:
 *
 * - a string with `@` in it is an email, not a name: no name;
 * - leading titles are skipped (CA, CS, CMA, Dr, Adv, Mr, Mrs, Ms, Shri, Smt,
 *   with or without their dots, several in a row);
 * - a token with no letter in it is not a name either.
 *
 * No name means "Good morning" alone. The header's own derivation calls this
 * too, so the two never disagree (FLOW_SCREEN_SPEC.md §1.4).
 */

const TITLES: ReadonlySet<string> = new Set([
    'ca', 'cs', 'cma', 'dr', 'adv', 'mr', 'mrs', 'ms', 'shri', 'smt',
]);

/** "C.A." → "ca", "Dr." → "dr", "Shri," → "shri". */
function bare(token: string): string {
    return token.toLowerCase().replace(/[.,]/g, '');
}

export function firstNameFor(fullName: string | null | undefined): string {
    const s = (fullName ?? '').trim();
    if (!s || s.includes('@')) return '';
    const tokens = s.split(/\s+/);
    let i = 0;
    while (i < tokens.length && TITLES.has(bare(tokens[i]))) i += 1;
    const name = (tokens[i] ?? '').replace(/[.,;:]+$/, '');
    return /\p{L}/u.test(name) ? name : '';
}
